import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import TeamAvatar from 'app/components/TeamAvatar';
import LiveTag from 'app/components/LiveTag';
import { withBackgroundImage } from 'app/utils/style';
import { getStatWinner } from 'app/utils/entities/match_data';
import { localizedMapName } from 'app/i18n/utils';
import { round } from 'app/utils/number';
import { getCDNUrl } from 'app/utils/api_url';
import {
  getCsMapWinrate,
  getCsRoundScore,
  hasField
} from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/utils';

import style from './style.module.scss';

interface Props {
  // Every game of the series, in order — one card each.
  games: AggregatedGameCs[];
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  currentGameId: number | undefined;
}

// The series' maps. A finished game shows its winner and final round score; any
// other game shows the map name and, when the feed carries it, each team's
// 3-month winrate on that map.
//
// The winrate is read per card, not once for the row: `map_winrate` is decoupled
// from `detailed` and present on every game, but `winrate` itself can be null
// per map when a team has no history there.
const CsMapRow: FC<Props> = ({ games, opponent1, opponent2, currentGameId }) => {
  const { t } = useTranslation();

  const cards = games.map(game => {
    const team1 = game.teams.find(team => team.id === opponent1?.id);
    const team2 = game.teams.find(team => team.id === opponent2?.id);
    const winrate1 = getCsMapWinrate(team1)?.winrate ?? null;
    const winrate2 = getCsMapWinrate(team2)?.winrate ?? null;
    const hasMapId = hasField(game.available_fields, 'map_id');

    return {
      game,
      team1,
      team2,
      winrate1,
      winrate2,
      hasMapId,
      hasWinrate:
        hasField(game.available_fields, 'map_winrate') && (winrate1 !== null || winrate2 !== null),
      // winner_id can be set while status still reads running, so trust the id.
      winner: [opponent1, opponent2].find(opponent => opponent?.id === game.winner_id)
    };
  });

  const showCaption = cards.some(card => card.hasWinrate && !card.winner);

  const renderWinrate = (value: number | null, highlighted: boolean, teamIndex: 0 | 1) => (
    <span className={style.winrateItem}>
      <span className={classNames(style.winrateValue, { [style.winrateValueLead]: highlighted })}>
        {value !== null ? `${round(value * 100, 0)}%` : '—'}
      </span>
      <TeamAvatar team={teamIndex === 0 ? opponent1 : opponent2} className={style.winrateLogo} />
    </span>
  );

  return (
    <div className={style.row}>
      <div className={style.cards}>
        {cards.map(({ game, team1, team2, winrate1, winrate2, hasMapId, hasWinrate, winner }) => {
          const [lead1, lead2] = getStatWinner({
            highlighted: 'max',
            team1Value: winrate1,
            team2Value: winrate2
          });
          const [scoreLead1, scoreLead2] = getStatWinner({
            highlighted: 'max',
            team1Value: getCsRoundScore(team1),
            team2Value: getCsRoundScore(team2)
          });
          const imageUrl = hasMapId && game.map ? getCDNUrl(game.map.image_url) : undefined;

          return (
            <div key={game.id} className={style.card}>
              <div
                className={classNames(style.thumb, {
                  [style.noImage]: !imageUrl,
                  [style.thumbLive]: game.id === currentGameId && !winner
                })}
                style={imageUrl ? withBackgroundImage(imageUrl) : undefined}
              >
                {winner ? (
                  <TeamAvatar team={winner} className={style.winnerLogo} />
                ) : (
                  hasMapId &&
                  game.map && <span className={style.mapName}>{localizedMapName(game.map, t)}</span>
                )}
                {game.id === currentGameId && !winner && <LiveTag className={style.liveTag} />}
              </div>

              {winner ? (
                <span className={style.score}>
                  <span
                    className={classNames({
                      [style.winner]: scoreLead1,
                      [style.loser]: scoreLead2
                    })}
                  >
                    {getCsRoundScore(team1)}
                  </span>
                  <span>:</span>
                  <span
                    className={classNames({
                      [style.winner]: scoreLead2,
                      [style.loser]: scoreLead1
                    })}
                  >
                    {getCsRoundScore(team2)}
                  </span>
                </span>
              ) : (
                hasWinrate && (
                  <span className={style.winrate}>
                    {renderWinrate(winrate1, lead1, 0)}
                    <span className={style.winrateDivider} />
                    {renderWinrate(winrate2, lead2, 1)}
                  </span>
                )
              )}
            </div>
          );
        })}
      </div>
      {showCaption && <span className={style.caption}>{t('block-grad-name-3_month_winrate')}</span>}
    </div>
  );
};

CsMapRow.displayName = 'CsMapRow';

export default CsMapRow;
